import { phdResearchProjects } from "../data/phdResearchProjects";
import { schoolProjects } from "../data/schoolprojects";
import { researchProjects } from "../data/researchprojects";
import { otherProjects } from "../data/otherprojects";
import { newsItems } from "../data/news";
import { features } from "../data/features";
import { isProjectShown } from "./projects";
import { findPublicationByTitle } from "./publicationUtils";
import { projectCardBlurb } from "./prePhdProjects";
import {
  compareLooseDateItems,
  comparePublicationsByDate,
  getVenuesForPublications,
  isWorkshopPublication,
} from "./pillarContent";
import { publications } from "../data/publications";

const projectById = Object.fromEntries(
  phdResearchProjects.map((project) => [project.id, project]),
);

const prePhdById = Object.fromEntries(
  [...schoolProjects, ...researchProjects, ...otherProjects].map((project) => [
    project.id,
    project,
  ]),
);

const normalizeTitle = (title = "") =>
  String(title)
    .replace(/<[^>]+>/g, "")
    .replace(/['"“”‘’]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const resolveByTitle = (titles = [], collection = []) => {
  const seen = new Set();
  const resolved = [];
  for (const title of titles) {
    const key = normalizeTitle(title);
    if (!key || seen.has(key)) continue;
    const match = collection.find(
      (item) => normalizeTitle(item.title) === key,
    );
    if (match) {
      seen.add(key);
      resolved.push(match);
    } else {
      seen.add(key);
      resolved.push({ title: String(title) });
    }
  }
  return resolved;
};

const addPublicationRef = (refs, seen, ref) => {
  const title = typeof ref === "string" ? ref : ref?.title;
  const key = normalizeTitle(title);
  if (!key || seen.has(key)) return;
  seen.add(key);
  refs.push(typeof ref === "string" ? { title } : ref);
};

/** Inline `publications` plus association titles, first-seen wins. */
export const collectProjectPublicationRefs = (project) => {
  const refs = [];
  const seen = new Set();
  (project?.publications || []).forEach((ref) =>
    addPublicationRef(refs, seen, ref),
  );
  (project?.associations?.publications || []).forEach((ref) =>
    addPublicationRef(refs, seen, ref),
  );
  return refs;
};

export const partitionProjectPublications = (refs = []) => {
  const resolved = [];
  const seen = new Set();
  for (const ref of refs) {
    const title = typeof ref === "string" ? ref : ref?.title;
    const key = normalizeTitle(title);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const matched = findPublicationByTitle(title);
    resolved.push(
      matched || (typeof ref === "object" && ref ? ref : { title }),
    );
  }
  resolved.sort(comparePublicationsByDate);
  return {
    relatedPublications: resolved.filter(
      (pub) => !isWorkshopPublication(pub),
    ),
    workshops: resolved.filter(isWorkshopPublication),
  };
};

/** Venue pills for project cards, matching pillar-card CHI / DIS / CSCW chips. */
export const getProjectVenues = (project, pubs = publications) => {
  const titles = new Set(
    collectProjectPublicationRefs(project).map((ref) =>
      normalizeTitle(ref.title),
    ),
  );
  return getVenuesForPublications(
    pubs.filter((pub) => titles.has(normalizeTitle(pub.title))),
  );
};

/** Newest first; same-date items keep the source collection's reverse-chrono order. */
const sortByDateDesc = (items, collection = []) => {
  const indexByTitle = new Map(
    collection.map((item, i) => [normalizeTitle(item.title), i]),
  );
  return [...items].sort((a, b) => {
    const byDate = compareLooseDateItems(a, b);
    if (byDate !== 0) return byDate;
    const ia = indexByTitle.get(normalizeTitle(a.title)) ?? Number.POSITIVE_INFINITY;
    const ib = indexByTitle.get(normalizeTitle(b.title)) ?? Number.POSITIVE_INFINITY;
    return ia - ib;
  });
};

/**
 * Cross-linked content for a project briefing modal (PhD or pre-PhD).
 * Contributions are intentionally omitted from the public modal for now.
 */
export const getProjectContent = (projectId) => {
  const phd = projectById[projectId];
  if (phd && isProjectShown(phd)) {
    const { relatedPublications, workshops } = partitionProjectPublications(
      collectProjectPublicationRefs(phd),
    );

    return {
      project: phd,
      relatedPublications,
      workshops,
      talks: sortByDateDesc(
        resolveByTitle(phd.associations?.news || [], newsItems),
        newsItems,
      ),
      mediaCoverage: sortByDateDesc(
        resolveByTitle(phd.associations?.features || [], features),
        features,
      ),
      collection: "phd",
    };
  }

  const prePhd = prePhdById[projectId];
  if (!prePhd) return null;

  return {
    project: {
      ...prePhd,
      summary: projectCardBlurb(prePhd.description) || prePhd.description,
      duration: prePhd.year,
    },
    relatedPublications: [],
    workshops: [],
    talks: [],
    mediaCoverage: [],
    collection: "pre-phd",
  };
};

export const getPhdProjectById = (projectId) => {
  const project = projectById[projectId];
  if (!project || !isProjectShown(project)) return null;
  return project;
};
